// Logica pura de la bandeja del proveedor (PP-34): reloj de la oferta contra
// el instante del servidor, que acciones habilita la UI para cada viaje, orden
// de la bandeja y fechas. Sin React ni reloj ambiente: `nowMs` llega por
// parametro (regla de frontend).
//
// La UI deshabilita, la API decide: `tripActions` solo evita ofrecer un boton
// que la API va a rechazar seguro. Si el estado cambio en el medio, la API
// responde el 409 tipado y la bandeja vuelve a pedir el estado.
import type { ProviderTrip, ProviderTripDetailResponse, ProviderTripSummary } from "@ucorp/contracts";
import { VEHICLE_TYPE_LABEL } from "./fleet.js";

/** Segundos que le quedan al reloj de la oferta, redondeados hacia arriba y nunca negativos. */
export function remainingSeconds(deadlineAt: string, nowMs: number): number {
  const deadline = Date.parse(deadlineAt);
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - nowMs) / 1000));
}

const pad = (n: number): string => String(n).padStart(2, "0");

/** "0:45", "12:03" o, desde una hora, "1 h 05 min" (los plazos van de 30 s a 24 h). */
export function formatRemaining(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total >= 3600) {
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${String(hours)} h ${pad(minutes)} min`;
  }
  return `${String(Math.floor(total / 60))}:${pad(total % 60)}`;
}

/** Clave de un reloj concreto: cambia si la oferta, su estado o su plazo cambian. */
export function deadlineKey(trip: ProviderTrip): string | undefined {
  if (trip.offer === undefined) return undefined;
  return `${trip.tripId}:${String(trip.offer.offerId)}:${trip.offer.state}:${trip.offer.deadlineAt}`;
}

/** Relojes que ya llegaron a cero: la bandeja le pregunta al servidor que paso con ellos. */
export function dueDeadlines(trips: readonly ProviderTrip[], nowMs: number): string[] {
  const due: string[] = [];
  for (const trip of trips) {
    const key = deadlineKey(trip);
    if (key !== undefined && trip.offer !== undefined && remainingSeconds(trip.offer.deadlineAt, nowMs) === 0) due.push(key);
  }
  return due;
}

export interface TripActions {
  /** Aceptar o rechazar: oferta viva sin aceptar y con reloj. */
  respond: boolean;
  /** Primera asignacion: oferta aceptada y con reloj de asignacion. */
  assign: boolean;
  /** Cambio de vehiculo o conductor: ya asignado y el viaje sigue programado. */
  reassign: boolean;
}

export function tripActions(trip: ProviderTrip, nowMs: number): TripActions {
  const live = trip.offer !== undefined && remainingSeconds(trip.offer.deadlineAt, nowMs) > 0;
  return {
    respond: trip.assignmentStatus === "offered" && trip.offer?.state === "offered" && live,
    assign: trip.assignmentStatus === "accepted" && trip.offer?.state === "accepted" && live,
    reassign: trip.assignmentStatus === "assigned" && trip.status === "scheduled",
  };
}

/** Primero lo que pide una respuesta (el reloj mas corto arriba), despues lo aceptado, lo asignado y el resto. */
const RANK: Record<ProviderTrip["assignmentStatus"], number> = {
  offered: 0,
  accepted: 1,
  assigned: 2,
  unoffered: 3,
  exhausted: 4,
  void: 5,
};

export function sortInbox(trips: readonly ProviderTrip[]): ProviderTrip[] {
  return [...trips].sort((a, b) => {
    if (RANK[a.assignmentStatus] !== RANK[b.assignmentStatus]) return RANK[a.assignmentStatus] - RANK[b.assignmentStatus];
    const da = a.offer?.deadlineAt;
    const db = b.offer?.deadlineAt;
    if (da !== undefined && db !== undefined && da !== db) return Date.parse(da) - Date.parse(db);
    return a.tripId < b.tripId ? -1 : a.tripId > b.tripId ? 1 : 0;
  });
}

/** Cuantos viajes esperan una accion del proveedor (responder o asignar). */
export function pendingCount(trips: readonly ProviderTrip[], nowMs: number): number {
  return trips.filter((trip) => {
    const actions = tripActions(trip, nowMs);
    return actions.respond || actions.assign;
  }).length;
}

/** Dia calendario siguiente a una fecha YYYY-MM-DD, sin Date local: sin corrimientos de zona. */
export function nextDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const next = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + 1));
  return next.toISOString().slice(0, 10);
}

/** YYYY-MM-DD a DD/MM/YYYY sin pasar por Date: la fecha ya es la operativa de la planta. */
export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day ?? ""}/${month ?? ""}/${year ?? ""}`;
}

/** Hora HH:MM de un instante en la zona de la planta (la que manda la API, nunca la del navegador). */
export function formatClock(instant: string, timezone: string): string {
  const time = Date.parse(instant);
  if (Number.isNaN(time)) return "";
  try {
    return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: timezone }).format(time);
  } catch {
    return "";
  }
}

/**
 * Bandeja = ofertas vivas de cualquier dia + viajes del dia elegido (PP-34b).
 * Un viaje que viene por las dos lecturas aparece una sola vez, con la fila de
 * las ofertas vivas: es la que fija el reloj que corre.
 */
export function mergeInbox(live: readonly ProviderTrip[], day: readonly ProviderTrip[]): ProviderTrip[] {
  const seen = new Set(live.map((trip) => trip.tripId));
  return [...live, ...day.filter((trip) => !seen.has(trip.tripId))];
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? `1 ${one}` : `${String(n)} ${many}`);

/**
 * Resumen de la fila para decidir sin abrir el recorrido: sentido, horas en
 * la zona de la planta, paradas y pasajeros. Sin datos de personas: la API
 * no los manda y aca no se inventan.
 */
export function summaryParts(summary: ProviderTripSummary | undefined, timezone: string): string[] {
  if (summary === undefined) return [];
  const parts: string[] = [];
  if (summary.direction !== undefined) parts.push(summary.direction === "inbound" ? "Ingreso" : "Egreso");
  if (summary.departureAt !== undefined) {
    const clock = formatClock(summary.departureAt, timezone);
    if (clock !== "") parts.push(`Sale ${clock}`);
  }
  if (summary.siteArrivalAt !== undefined) {
    const clock = formatClock(summary.siteArrivalAt, timezone);
    if (clock !== "") parts.push(`Llega a planta ${clock}`);
  }
  parts.push(plural(summary.stopCount, "parada", "paradas"));
  parts.push(plural(summary.passengerCount, "pasajero", "pasajeros"));
  if (summary.firstStopName !== undefined) parts.push(`Primera parada: ${summary.firstStopName}`);
  return parts;
}

// PB-29 (H7): el portal nunca muestra un id tecnico. El viaje se nombra por
// servicio, sentido, hora de salida y pasajeros; la parada por su papel: la
// planta es origen o destino, una unica subida es el origen, una unica bajada
// el destino, y si hay varias se numeran.

/** Sentido legible: con la planta al final ingreso, al principio egreso; sin planta, traslado. */
function directionWord(summary: ProviderTripSummary): string {
  if (summary.direction === "inbound") return "Ingreso";
  if (summary.direction === "outbound") return "Egreso";
  return "Traslado";
}

/** Nombre humano del viaje: "Remis · Ingreso 14:49 · 1 pasajero", en la zona de la planta. */
export function tripTitle(trip: Pick<ProviderTrip, "vehicleType" | "summary">, timezone: string | undefined): string {
  const parts = [VEHICLE_TYPE_LABEL[trip.vehicleType]];
  const summary = trip.summary;
  if (summary === undefined) return `Viaje en ${parts[0]!.toLowerCase()}`;
  const clock = summary.departureAt === undefined || timezone === undefined ? "" : formatClock(summary.departureAt, timezone);
  parts.push(clock === "" ? directionWord(summary) : `${directionWord(summary)} ${clock}`);
  parts.push(plural(summary.passengerCount, "pasajero", "pasajeros"));
  return parts.join(" · ");
}

type KindedStop = Pick<ProviderTripDetailResponse["stops"][number], "stopId" | "order" | "kind">;

/**
 * Nombre de cada parada del recorrido, por stopId. Usa `kind` (contratos
 * 0.6.26); sin el, con el sentido del resumen ubica la planta al final
 * (ingreso) o al principio (egreso).
 */
export function stopNames(stops: readonly KindedStop[], direction?: ProviderTripSummary["direction"]): Map<string, string> {
  const ordered = [...stops].sort((a, b) => a.order - b.order);
  const last = ordered.length - 1;
  const kindOf = (stop: KindedStop, i: number): NonNullable<KindedStop["kind"]> => {
    if (stop.kind !== undefined) return stop.kind;
    if (direction === "inbound") return i === last ? "site" : "pickup";
    if (direction === "outbound") return i === 0 ? "site" : "dropoff";
    return i === last && i > 0 ? "dropoff" : "pickup";
  };
  const kinds = ordered.map(kindOf);
  const pickups = kinds.filter((k) => k === "pickup").length;
  const dropoffs = kinds.filter((k) => k === "dropoff").length;
  const names = new Map<string, string>();
  let n = 0;
  ordered.forEach((stop, i) => {
    const kind = kinds[i];
    if (kind === "site") {
      names.set(stop.stopId, i === 0 && last > 0 ? "Planta (origen)" : "Planta (destino)");
      return;
    }
    n += 1;
    if (kind === "pickup" && pickups === 1 && i === 0) names.set(stop.stopId, "Origen");
    else if (kind === "dropoff" && dropoffs === 1 && i === last) names.set(stop.stopId, "Destino");
    else names.set(stop.stopId, `Parada ${String(n)}`);
  });
  return names;
}
